import { ConfigurationItemUi, getReadCiList1QueryKey, useReadCiList1 } from '@isdd/metais-common/api/generated/cmdb-swagger'
import { AttributeProfile, CiType } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { flattenDeep, intersectionWith } from 'lodash'

type AttributeTechnicalName = string
type CiTypeConstraintTechnicalName = string
type CiUuid = string
type CiItemUuid = string

export type CiConstraintType = {
    ciType: string
    type: string
}

export const isConstraintCiType = (obj: unknown): obj is CiConstraintType => {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'ciType' in obj &&
        typeof (obj as CiConstraintType).ciType === 'string' &&
        'type' in obj &&
        typeof (obj as CiConstraintType).type === 'string'
    )
}

const isObjectEmpty = (obj: { [key: string]: unknown }): boolean => {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) return false
    }
    return true
}

const isObject = (value: unknown): value is { [key: string]: unknown } => {
    return value !== null && typeof value === 'object'
}

const hasMeaningfulValue = (value: unknown): boolean => {
    if (value === null || value === undefined || value === false || value === 0 || value === '' || Number.isNaN(value)) {
        return false
    }
    if (Array.isArray(value) && value.length === 0) {
        return false
    }
    if (isObject(value) && value.constructor === Object && isObjectEmpty(value)) {
        return false
    }
    return true
}

const getCiTypesFromConstraints = (entityStructure: CiType | undefined): Record<AttributeTechnicalName, CiTypeConstraintTechnicalName> => {
    if (!entityStructure) return {}
    const { attributes, attributeProfiles } = entityStructure

    const ciTypesFromConstraints = [
        ...(attributes ?? []),
        ...(attributeProfiles?.map((profile: AttributeProfile) => profile.attributes).flat() ?? []),
    ]
        .filter((att) => att?.constraints?.[0]?.type === 'ciType' && att?.valid && !att.invisible)
        .reduce((acc: Record<AttributeTechnicalName, CiTypeConstraintTechnicalName>, att) => {
            if (isConstraintCiType(att?.constraints?.[0])) {
                return { ...acc, [att?.technicalName ?? '']: att?.constraints?.[0]?.ciType ?? '' }
            }
            return acc
        }, {})

    return ciTypesFromConstraints
}

export const useGetCiTypeConstraintsData = (entityStructure: CiType | undefined, ciItemsList: ConfigurationItemUi[]) => {
    const ciTypesFromConstraints = getCiTypesFromConstraints(entityStructure)
    const recordOfCiConstraintUuidAndAttTechnicalName = ciItemsList.reduce(
        (acc: Record<CiItemUuid, unknown | Record<AttributeTechnicalName, CiUuid | string[]>>, ciItem) => {
            const pairsOfAttTechNameAndCiUuid = Object.keys(ciTypesFromConstraints).reduce(
                (obj: unknown | Record<AttributeTechnicalName, CiUuid | string[]>, technicalName) => {
                    if (ciItem?.attributes?.[technicalName] && hasMeaningfulValue(ciItem.attributes?.[technicalName])) {
                        return { ...(obj as object), [technicalName]: ciItem.attributes?.[technicalName] }
                    }
                    return obj
                },
                {},
            )
            if (!isObjectEmpty(pairsOfAttTechNameAndCiUuid as unknown as { [key: string]: unknown })) {
                return { ...acc, [ciItem.uuid ?? '']: pairsOfAttTechNameAndCiUuid }
            }

            return acc
        },
        {},
    ) as { [key: string]: Record<string, string | string[]> }

    const listOfAllCiUuidsToSearch = flattenDeep(
        Object.values(recordOfCiConstraintUuidAndAttTechnicalName).map((pair) => Object.values(pair)),
    ) as string[]

    const isQueryEnabled = listOfAllCiUuidsToSearch.length > 0
    const { data, isLoading, isError, fetchStatus } = useReadCiList1(
        {
            filter: {
                type: [...new Set(Object.values(ciTypesFromConstraints))],
                uuid: [...new Set(listOfAllCiUuidsToSearch)],
            },
            perpage: 999,
        },
        {
            query: {
                queryKey: getReadCiList1QueryKey({
                    filter: {
                        type: [...new Set(Object.values(ciTypesFromConstraints))],
                        uuid: [...new Set(listOfAllCiUuidsToSearch)],
                    },
                    perpage: 999,
                }),
                enabled: isQueryEnabled,
            },
        },
    )

    const uuidsToMatchedCiItemsMap: Record<string, Record<string, ConfigurationItemUi | ConfigurationItemUi[]>> = {}

    for (const displayedCiItemUuid in recordOfCiConstraintUuidAndAttTechnicalName) {
        const currentUuidMap: Record<string, ConfigurationItemUi | ConfigurationItemUi[]> = {}
        for (const techName in recordOfCiConstraintUuidAndAttTechnicalName[displayedCiItemUuid]) {
            if (Array.isArray(recordOfCiConstraintUuidAndAttTechnicalName[displayedCiItemUuid][techName])) {
                const uuids = recordOfCiConstraintUuidAndAttTechnicalName[displayedCiItemUuid][techName]

                const inter = (data?.configurationItemSet ?? []).filter((ci) => uuids.includes(ci.uuid ?? ''))
                if (inter) {
                    currentUuidMap[techName] = inter
                }
            } else {
                const matchedCiItemByUuid = data?.configurationItemSet?.find(
                    (item) => item.uuid === recordOfCiConstraintUuidAndAttTechnicalName[displayedCiItemUuid][techName],
                )
                if (matchedCiItemByUuid) {
                    currentUuidMap[techName] = matchedCiItemByUuid
                }
            }
        }
        uuidsToMatchedCiItemsMap[displayedCiItemUuid] = currentUuidMap
    }

    return {
        isLoading: fetchStatus != 'idle' && isLoading,
        isError,
        uuidsToMatchedCiItemsMap,
    }
}
